import React from 'react';

import {
  Alert,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  Title,
  px,
  useMantineTheme,
} from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import usePaymentMethod from 'hooks/paymentMethod/usePaymentMethod';
import useUnlockPaymentMethod from 'hooks/paymentMethod/useUnlockPaymentMethod';
import { Link } from 'react-router-dom';
import notificationHandler from 'utils/notifications';

interface IUnlockPaymentMethodModalProps {
  paymentMethodId: string;
  opened: boolean;
  close: () => void;
  userRole: 'staff' | 'athlete';
}

export const isAthleteUnlockablePaymentMethod = (failureCode: string): boolean => {
  return ['expired_card', 'insufficient_funds', 'incorrect_zip'].includes(failureCode);
};

const UnlockPaymentMethodModal = ({
  paymentMethodId,
  opened,
  close,
  userRole,
}: IUnlockPaymentMethodModalProps): JSX.Element => {
  const theme = useMantineTheme();

  const { data: paymentMethodData, isLoading, isSuccess, isError } = usePaymentMethod(paymentMethodId);
  const unlockPaymentMethodMutation = useUnlockPaymentMethod();

  const handleUnlock = (): void => {
    const { successNotification, errorNotification } = notificationHandler();
    unlockPaymentMethodMutation.mutate(paymentMethodId, {
      onSuccess: () => {
        successNotification('Payment method successfully unlocked.');
        close();
      },
      onError: error => {
        errorNotification('Payment method failed to unlock', error.response?.data.message);
      },
    });
  };

  const isUnlockEnabled =
    userRole === 'staff' || isAthleteUnlockablePaymentMethod(paymentMethodData?.data?.failureCode || '');

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<Title order={4}>Payment Method Locked</Title>}
      trapFocus
      centered
      size='lg'
    >
      <LoadingOverlay visible={isLoading} />

      {isSuccess && paymentMethodData && (
        <>
          <Stack px='md' w='100%' spacing='md' py='lg'>
            <Text>A payment using this Payment Method recently failed.</Text>
            <Text color='dimmed' pl='md'>
              Payment Failure Decline Code: {paymentMethodData.data.failureCode}
            </Text>
            <Text color='dimmed' pl='md'>
              Payment Failure Message: {paymentMethodData.data.failureMessage}
            </Text>
            {userRole === 'staff' && (
              <>
                <Text>
                  Documentation found here{' '}
                  <Link
                    target='_blank'
                    rel='noopener noreferrer'
                    to='https://docs.stripe.com/declines/codes'
                  >
                    [https://docs.stripe.com/declines/codes]
                  </Link>{' '}
                  provides more information on how to resolve issues.
                </Text>
                <Text>Kilo Customer Support is also available to help resolve issues.</Text>
                <Text>
                  Once issues are resolved click “Confirm and Unlock” to unlock this payment method.
                </Text>
              </>
            )}
            {userRole === 'athlete' && isUnlockEnabled && (
              <>
                <Text>
                  To resolve this issue, please update your payment method or check with your bank for more
                  details.
                </Text>
                <Text>
                  Once the issue is resolved, click &quot;Confirm and Unlock&quot; to restore this payment
                  method for future transactions.
                </Text>
              </>
            )}
            {userRole === 'athlete' && !isUnlockEnabled && (
              <Text pb='lg'>Unfortunately, this payment method cannot be unlocked at this time.</Text>
            )}
          </Stack>
          <Group position='right'>
            <Button variant='outline' onClick={close} disabled={unlockPaymentMethodMutation.isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleUnlock}
              disabled={!isUnlockEnabled}
              loading={unlockPaymentMethodMutation.isLoading}
            >
              Confirm & Unlock
            </Button>
          </Group>
        </>
      )}
      {isError && (
        <Alert icon={<IconExclamationCircle size={px(theme.fontSizes.md)} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      )}
    </Modal>
  );
};

export default UnlockPaymentMethodModal;
